import React from "react";
import { AlertTriangle, CreditCard, RefreshCcw } from "lucide-react";

/**
 * SensitiveOverwriteDialog
 * Modal-over-modal that asks the user what to do with a single conflicting
 * payroll row whose conflict type is PAID, HAS_REPAYMENT or PAID_AND_REPAYMENT.
 *
 * Props:
 *   conflict       – conflictInfo object from the /conflict-check response
 *   onSkip()       – called when user chooses "Skip this employee"
 *   onOverwrite()  – called when user chooses "Yes, overwrite"
 *   currentTheme   – the active mo3ti theme object
 *   isAr           – boolean: Arabic UI
 */
const SensitiveOverwriteDialog = ({
  conflict,
  onSkip,
  onOverwrite,
  currentTheme,
  isAr,
}) => {
  if (!conflict) return null;

  const isPaid   = conflict.isPaid;
  const hasRepm  = conflict.hasAdvancementRepayment;
  const bothRisks = isPaid && hasRepm;

  const monthName = (m) => {
    const names = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December",
    ];
    const namesAr = [
      "يناير","فبراير","مارس","أبريل","مايو","يونيو",
      "يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر",
    ];
    return isAr ? (namesAr[m - 1] || m) : (names[m - 1] || m);
  };

  const periodLabel = `${monthName(conflict.month)} ${conflict.year}`;
  const importedOnLabel = conflict.importedAt
    ? new Date(conflict.importedAt).toLocaleDateString(isAr ? "ar-EG" : "en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  return (
    /* Overlay – sits on top of the import modal */
    <div
      className="payroll-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
        backdropFilter: "blur(3px)",
      }}
    >
      <div
        style={{
          background: currentTheme.colors.cardBackground || currentTheme.colors.inputBackground,
          border: `2px solid #f59e0b`,
          borderRadius: "16px",
          width: "100%",
          maxWidth: "520px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 16px",
            background: "rgba(245,158,11,0.12)",
            borderBottom: `1px solid rgba(245,158,11,0.3)`,
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <AlertTriangle size={24} color="#f59e0b" />
          <div>
            <div
              style={{
                fontWeight: "700",
                fontSize: "16px",
                color: currentTheme.colors.textPrimary,
              }}
            >
              {isAr ? "استبدال راتب محمي" : "Overwrite Protected Payroll"}
            </div>
            <div style={{ fontSize: "13px", color: currentTheme.colors.textSecondary, marginTop: "2px" }}>
              {conflict.clientCode} — {periodLabel}
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px" }}>
          {/* PAID warning */}
          {isPaid && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                padding: "14px",
                borderRadius: "10px",
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.3)",
                marginBottom: bothRisks ? "12px" : "16px",
              }}
            >
              <CreditCard size={18} color="#ef4444" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div style={{ fontSize: "13px", color: currentTheme.colors.textPrimary, lineHeight: "1.6" }}>
                {isAr ? (
                  <>
                    <strong style={{ color: "#ef4444" }}>هذا الراتب مدفوع بالفعل.</strong>
                    {importedOnLabel && (
                      <> تم الاستيراد في: {importedOnLabel}
                        {conflict.importedBy ? ` بواسطة ${conflict.importedBy}` : ""}.
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <strong style={{ color: "#ef4444" }}>This payroll has already been PAID.</strong>
                    {importedOnLabel && (
                      <> Imported on {importedOnLabel}
                        {conflict.importedBy ? ` by ${conflict.importedBy}` : ""}.
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* HAS_REPAYMENT warning */}
          {hasRepm && (
            <div
              style={{
                display: "flex",
                gap: "12px",
                padding: "14px",
                borderRadius: "10px",
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.3)",
                marginBottom: "16px",
              }}
            >
              <RefreshCcw size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: "2px" }} />
              <div style={{ fontSize: "13px", color: currentTheme.colors.textPrimary, lineHeight: "1.6" }}>
                {isAr ? (
                  <>
                    <strong style={{ color: "#f59e0b" }}>
                      يرتبط بهذا الراتب {conflict.repaymentCount || "بعض"} سجل(ات) سداد سلفة قائمة.
                    </strong>
                    {" "}ستُحذف أقساط السلفة المخصومة من هذا الراتب (لن يُخصم القسط هذا الشهر إلا إذا كان الملف يخصمه)، وتظل سجلات السداد المسجلة مرتبطة بنفس الراتب.
                  </>
                ) : (
                  <>
                    <strong style={{ color: "#f59e0b" }}>
                      This payroll carries advance installments or repayments ({(conflict.advanceInstallmentCount || 0) + (conflict.repaymentCount || 0) || "some"}).
                    </strong>
                    {" "}The advance installment deduction(s) on it will be removed — the installment is not deducted this month unless your file deducts it. Recorded repayment records stay linked to this same payroll.
                  </>
                )}
              </div>
            </div>
          )}

          {/* Consequence notice */}
          <div
            style={{
              fontSize: "13px",
              color: currentTheme.colors.textSecondary,
              lineHeight: "1.65",
              padding: "12px 14px",
              borderRadius: "8px",
              background: currentTheme.colors.inputBackground,
              border: `1px solid ${currentTheme.colors.border}`,
              marginBottom: "20px",
            }}
          >
            {isAr
              ? "الاستبدال يُبقي نفس سجل الراتب لكنه يحذف كل بدلاته وخصوماته وقيمه، ثم يكتب بيانات الملف فقط. تصبح حالة الدفع كما في عمود Payment Status بالملف (أو قيد الانتظار)، ثم يُقفل الراتب. لا يمكن التراجع؛ لتعديله استورد الملف مرة أخرى."
              : "Replacing keeps the same payroll record but removes all of its allowances, deductions and values, then writes only the file's data. Its payment status becomes the file's Payment Status (or Pending), and the payroll is then locked. This cannot be undone — import again to change it."}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
            <button
              onClick={onSkip}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: `1px solid ${currentTheme.colors.border}`,
                background: "transparent",
                color: currentTheme.colors.textSecondary,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = currentTheme.colors.inputBackground;
                e.currentTarget.style.color = currentTheme.colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = currentTheme.colors.textSecondary;
              }}
            >
              {isAr ? "تخطّي هذا الموظف" : "Skip this employee"}
            </button>
            <button
              onClick={onOverwrite}
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                border: "none",
                background: "#ef4444",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "700",
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            >
              {isAr ? "نعم، استبدال" : "Yes, overwrite"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SensitiveOverwriteDialog;
